/**
 * Collapsible element history section showing selected elements across all tests
 */
import { useState, useEffect, Fragment } from 'react';
import {
  ActionIcon,
  Box,
  Paper,
  Title,
  Table,
  ScrollArea,
  Text,
  Loader,
  Alert,
  Collapse,
  Group,
  Badge,
  Tooltip,
  SegmentedControl,
} from '@mantine/core';
import { IconAlertCircle, IconHistory, IconX, IconTable, IconChartLine } from '@tabler/icons-react';
import { format } from 'date-fns';
import { Sparkline } from './Sparkline';
import { ElementChartView } from './ElementChartView';
import { PROVIDER_LABELS } from '@/utils/icpProviderDetect';
import type { ElementHistoryResponse } from '@/types/sensor';

function providerLabel(provider: string): string {
  return (PROVIDER_LABELS as Record<string, string>)[provider] ?? provider ?? 'Unknown';
}

type ViewMode = 'table' | 'chart';
const STORAGE_KEY = 'icpHistoryViewMode';

interface ElementHistorySectionProps {
  isOpen: boolean;
  data: ElementHistoryResponse | undefined;
  dosingRates?: Record<string, Record<string, number>>;  // { element: { intervalIdx: ml/day } }
  isLoading: boolean;
  error: Error | null;
  selectedElements: string[];
  onClear: () => void;
}

export function ElementHistorySection({
  isOpen,
  data,
  dosingRates,
  isLoading,
  error,
  selectedElements,
  onClear,
}: ElementHistorySectionProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'chart' ? 'chart' : 'table';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, viewMode);
  }, [viewMode]);

  if (selectedElements.length === 0) {
    return null;
  }

  // Whether the loaded history spans ≥2 distinct providers; only then do we
  // surface a per-point provider tooltip in the table (avoids clutter for the
  // common single-provider case).
  const providersInData = new Set<string>();
  for (const element of data?.elements ?? []) {
    for (const point of element.data) {
      if (point.provider) providersInData.add(point.provider);
    }
  }
  const multiProvider = providersInData.size >= 2;

  return (
    <Collapse in={isOpen} transitionDuration={300}>
      <Paper p="md" withBorder mb="lg">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconHistory size={20} />
            <Title order={3}>Element History</Title>
            <Tooltip label="Clear selection">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={onClear}
                aria-label="Clear element selection"
              >
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group gap="sm">
            <Badge variant="light" color="blue">
              {selectedElements.length} element{selectedElements.length !== 1 ? 's' : ''} selected
            </Badge>
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={(value) => setViewMode(value as ViewMode)}
              data={[
                {
                  value: 'table',
                  label: (
                    <Group gap={4} wrap="nowrap">
                      <IconTable size={14} />
                      <Text size="xs" visibleFrom="sm">Table</Text>
                    </Group>
                  ),
                },
                {
                  value: 'chart',
                  label: (
                    <Group gap={4} wrap="nowrap">
                      <IconChartLine size={14} />
                      <Text size="xs" visibleFrom="sm">Charts</Text>
                    </Group>
                  ),
                },
              ]}
            />
          </Group>
        </Group>

        {isLoading && (
          <Box py="xl" ta="center">
            <Loader size="md" />
            <Text size="sm" c="dimmed" mt="sm">
              Loading history...
            </Text>
          </Box>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error.message}
          </Alert>
        )}

        {data && data.elements.length > 0 && viewMode === 'table' && (
          <ScrollArea>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 140 }}>Element</Table.Th>
                  <Table.Th style={{ minWidth: 80 }}>Trend</Table.Th>
                  {data.test_dates.map((dateStr) => (
                    <Table.Th key={dateStr} style={{ minWidth: 80, textAlign: 'center' }}>
                      {format(new Date(dateStr), 'MMM d')}
                      <br />
                      <Text size="xs" c="dimmed">
                        {format(new Date(dateStr), 'yyyy')}
                      </Text>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.elements.map((element) => {
                  const elementDosingRates = dosingRates?.[element.element];
                  const hasDosingData = elementDosingRates && Object.keys(elementDosingRates).length > 0;

                  return (
                    <Fragment key={element.element}>
                      {/* ICP measurement row */}
                      <Table.Tr>
                        <Table.Td rowSpan={hasDosingData ? 2 : 1}>
                          <Text size="sm" fw={500}>
                            {element.label}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {element.unit}
                          </Text>
                        </Table.Td>
                        <Table.Td rowSpan={hasDosingData ? 2 : 1}>
                          <Sparkline
                            data={element.data.map((d) => ({ value: d.value }))}
                            color="var(--mantine-color-blue-6)"
                          />
                        </Table.Td>
                        {element.data.map((point) => (
                          <Table.Td
                            key={point.barcode}
                            style={{
                              textAlign: 'center',
                              backgroundColor:
                                point.in_range === false
                                  ? 'var(--mantine-color-red-light)'
                                  : undefined,
                            }}
                          >
                            {point.value !== null ? (
                              multiProvider ? (
                                <Tooltip label={providerLabel(point.provider)} withArrow>
                                  <Text
                                    size="sm"
                                    fw={point.in_range === false ? 700 : 400}
                                    c={point.in_range === false ? 'red' : undefined}
                                    style={{ cursor: 'default' }}
                                  >
                                    {point.value.toFixed(
                                      element.unit === 'ppb' ? 1 : element.unit === '' ? 2 : 1
                                    )}
                                  </Text>
                                </Tooltip>
                              ) : (
                                <Text
                                  size="sm"
                                  fw={point.in_range === false ? 700 : 400}
                                  c={point.in_range === false ? 'red' : undefined}
                                >
                                  {point.value.toFixed(
                                    element.unit === 'ppb' ? 1 : element.unit === '' ? 2 : 1
                                  )}
                                </Text>
                              )
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                        ))}
                      </Table.Tr>

                      {/* Dosing rate sub-row */}
                      {hasDosingData && (
                        <Table.Tr
                          style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
                        >
                          {/* Element + Trend columns are spanned from above via rowSpan */}
                          {element.data.map((point, idx) => {
                            // Interval index: this test date maps to interval idx-1
                            // (interval 0 = between test 0 and test 1, shown under test 1's column)
                            const intervalIdx = idx - 1;
                            const rate = intervalIdx >= 0 ? elementDosingRates[String(intervalIdx)] : undefined;
                            return (
                              <Table.Td
                                key={`dosing-${point.barcode}`}
                                style={{ textAlign: 'center' }}
                              >
                                {rate !== undefined ? (
                                  <Text size="xs" c="teal" ff="var(--mantine-font-family-monospace)">
                                    {rate.toFixed(2)} ml/d
                                  </Text>
                                ) : (
                                  <Text size="xs" c="dimmed">
                                    —
                                  </Text>
                                )}
                              </Table.Td>
                            );
                          })}
                        </Table.Tr>
                      )}
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        {data && data.elements.length > 0 && viewMode === 'chart' && (
          <ElementChartView data={data} />
        )}

        {data && data.elements.length === 0 && (
          <Text c="dimmed" ta="center" py="lg">
            No history data available for selected elements.
          </Text>
        )}
      </Paper>
    </Collapse>
  );
}
